import React, { useEffect, useState } from "react";
import { searchEmail } from "../../redux/constants/integrationApi";
import Modal from "react-bootstrap/Modal";
import ReactQuill from "react-quill";
import {
  BsJustifyLeft,
  BsListOl,
  BsPaperclip,
  BsSend,
  BsTypeBold,
  BsTypeItalic,
  BsTypeUnderline,
  BsX,
} from "react-icons/bs";

export const ContactSendEmailModal = ({
  show,
  onHide,
  composeForm,
  setComposeForm,
  composeAttachments,
  sendingEmail,
  composeError,
  onSendEmail,
  onAttachmentChange,
  onRemoveAttachment,
  composeEditorModules,
  composeMode,
  composeQuotedBody,
  t,
}) => {
  const [ccDraft, setCcDraft] = useState("");
  const [ccError, setCcError] = useState("");
  const [ccSuggestions, setCcSuggestions] = useState([]);
  const isToEditable = composeMode === "forward";

  const [toDraft, setToDraft] = useState("");
  const [toError, setToError] = useState("");
  const [toSuggestions, setToSuggestions] = useState([]);

  const fetchToSuggestions = async (query) => {
    if (!query || query.length < 2) {
      setToSuggestions([]);
      return;
    }
    try {
      const response = await searchEmail(query);
      setToSuggestions(response?.data?.results || []);
    } catch (err) {
      setToSuggestions([]);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isToEditable) fetchToSuggestions(toDraft.trim());
    }, 250);
    return () => clearTimeout(timer);
  }, [toDraft, isToEditable]);

  useEffect(() => {
    if (show) {
      setCcDraft("");
      setCcError("");
      setCcSuggestions([]);
      setToDraft("");
      setToError("");
      setToSuggestions([]);
    }
  }, [show]);

  const getToRecipients = () =>
    (composeForm.to || "")
      .split(/[;,]+/)
      .map((item) => item.trim())
      .filter(Boolean);

  const setToRecipients = (recipients) => {
    setComposeForm((form) => ({ ...form, to: recipients.join(", ") }));
  };

  const addToRecipient = (email) => {
    if (!email) return;
    setToError("");
    const current = getToRecipients();
    const currentLower = current.map((item) => item.toLowerCase());
    if (!currentLower.includes(email.toLowerCase())) {
      setToRecipients([...current, email]);
    }
    setToDraft("");
    setToSuggestions([]);
  };

  const commitToDraft = () => {
    const nextItems = toDraft
      .split(/[;,\s]+/)
      .map((item) => item.trim())
      .filter(Boolean);
    if (!nextItems.length) return;
    const invalidEmails = nextItems.filter((email) => !isValidEmail(email));
    if (invalidEmails.length) {
      setToError(`Invalid email: ${invalidEmails.join(", ")}`);
      return;
    }
    setToError("");
    const current = getToRecipients();
    const currentLower = current.map((item) => item.toLowerCase());
    const uniqueItems = nextItems.filter(
      (item) => !currentLower.includes(item.toLowerCase()),
    );
    setToRecipients([...current, ...uniqueItems]);
    setToDraft("");
  };

  const removeToRecipient = (recipientToRemove) => {
    setToRecipients(getToRecipients().filter((r) => r !== recipientToRemove));
  };

  const handleToKeyDown = (event) => {
    if (["Enter", "Tab", ",", ";", " "].includes(event.key)) {
      event.preventDefault();
      commitToDraft();
      return;
    }
    if (event.key === "Backspace" && !toDraft) {
      const recipients = getToRecipients();
      setToRecipients(recipients.slice(0, -1));
    }
  };

  const fetchCcSuggestions = async (query) => {
    if (!query || query.length < 2) {
      setCcSuggestions([]);
      return;
    }

    try {
      const response = await searchEmail(query);
      setCcSuggestions(response?.data?.results || []);
    } catch (err) {
      setCcSuggestions([]);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCcSuggestions(ccDraft.trim());
    }, 250);

    return () => clearTimeout(timer);
  }, [ccDraft]);

  const addCcRecipient = (email) => {
    if (!email) return;

    setCcError("");

    const current = getCcRecipients();
    const currentLower = current.map((item) => item.toLowerCase());

    if (!currentLower.includes(email.toLowerCase())) {
      setCcRecipients([...current, email]);
    }

    setCcDraft("");
    setCcSuggestions([]);
  };

  const getCcRecipients = () =>
    (composeForm.cc || "")
      .split(/[;,]+/)
      .map((item) => item.trim())
      .filter(Boolean);

  const setCcRecipients = (recipients) => {
    setComposeForm((form) => ({
      ...form,
      cc: recipients.join(", "),
    }));
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const commitCcDraft = () => {
    const nextItems = ccDraft
      .split(/[;,\s]+/)
      .map((item) => item.trim())
      .filter(Boolean);

    if (!nextItems.length) return;

    const invalidEmails = nextItems.filter((email) => !isValidEmail(email));

    if (invalidEmails.length) {
      setCcError(`Invalid email: ${invalidEmails.join(", ")}`);
      return;
    }

    setCcError("");

    const current = getCcRecipients();
    const currentLower = current.map((item) => item.toLowerCase());

    const uniqueItems = nextItems.filter(
      (item) => !currentLower.includes(item.toLowerCase()),
      // (item) => !currentLower.includes(item.toLowerCase()) && isValidEmail(item),
    );

    setCcRecipients([...current, ...uniqueItems]);
    setCcDraft("");
  };

  const removeCcRecipient = (recipientToRemove) => {
    setCcRecipients(
      getCcRecipients().filter((recipient) => recipient !== recipientToRemove),
    );
  };

  const handleCcKeyDown = (event) => {
    if (["Enter", "Tab", ",", ";", " "].includes(event.key)) {
      event.preventDefault();
      commitCcDraft();
      return;
    }

    if (event.key === "Backspace" && !ccDraft) {
      const recipients = getCcRecipients();
      setCcRecipients(recipients.slice(0, -1));
    }
  };

  const ccRecipients = getCcRecipients();

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      dialogClassName="contact-email-reader-dialog"
      contentClassName="contact-email-reader-content"
    >
      <div className="contact-email-compose">
        <div className="contact-email-compose-header">
          <div className="contact-email-compose-subject-row">
            <span>{t("Subject")}</span>
            <input
              value={composeForm.subject}
              onChange={(e) =>
                setComposeForm((form) => ({
                  ...form,
                  subject: e.target.value,
                }))
              }
            />
          </div>

          <button
            type="button"
            className="contact-email-compose-send"
            onClick={onSendEmail}
            disabled={sendingEmail}
          >
            <BsSend />
            {sendingEmail ? t("Sending") : t("Send")}
          </button>

          <button
            type="button"
            className="contact-email-reader-close"
            onClick={onHide}
            aria-label={t("Close")}
          >
            <BsX />
          </button>
        </div>

        <div className="contact-email-compose-fields">
          <div className="contact-email-compose-field-row">
            <span>{t("To")}</span>
            {isToEditable ? (
              <div className="contact-email-recipient-box">
                {getToRecipients().map((recipient) => (
                  <span
                    key={recipient}
                    className="contact-email-recipient-chip"
                  >
                    <span className="contact-email-recipient-avatar">
                      {recipient.charAt(0).toUpperCase()}
                    </span>
                    <span className="contact-email-recipient-name">
                      {recipient}
                    </span>
                    <button
                      type="button"
                      className="contact-email-recipient-remove"
                      onClick={() => removeToRecipient(recipient)}
                      aria-label={`${t("Remove")} ${recipient}`}
                    >
                      <BsX />
                    </button>
                  </span>
                ))}

                <input
                  value={toDraft}
                  onChange={(e) => {
                    const value = e.target.value;
                    setToDraft(value);
                    if (!value.trim()) setToError("");
                  }}
                  onKeyDown={handleToKeyDown}
                  onBlur={commitToDraft}
                  className="contact-email-recipient-input"
                  autoComplete="off"
                  style={{ minWidth: 120, flex: 1 }}
                />

                {toSuggestions.length > 0 && (
                  <div className="contact-email-cc-suggestions">
                    {toSuggestions.map((item) => (
                      <button
                        type="button"
                        key={item.id || item.email}
                        className="contact-email-cc-suggestion"
                        onMouseDown={() => addToRecipient(item.email)}
                      >
                        <div className="contact-email-cc-suggestion-avatar">
                          {item.name
                            ? item.name.charAt(0).toUpperCase()
                            : item.email.charAt(0).toUpperCase()}
                        </div>
                        <div className="contact-email-cc-suggestion-name">
                          {item.name || item.email}
                        </div>
                        <small>{item.email}</small>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <strong>{composeForm.to || ""}</strong>
            )}
          </div>

          {isToEditable && toError && (
            <div className="contact-emails-error-cc">{toError}</div>
          )}

          <div className="contact-email-compose-field-row contact-email-compose-cc-row">
            <span>{t("Cc")}</span>

            <div className="contact-email-recipient-box">
              {ccRecipients.map((recipient) => (
                <span key={recipient} className="contact-email-recipient-chip">
                  <span className="contact-email-recipient-avatar">
                    {recipient.charAt(0).toUpperCase()}
                  </span>

                  <span className="contact-email-recipient-name">
                    {recipient}
                  </span>

                  <button
                    type="button"
                    className="contact-email-recipient-remove"
                    onClick={() => removeCcRecipient(recipient)}
                    aria-label={`${t("Remove")} ${recipient}`}
                  >
                    <BsX />
                  </button>
                </span>
              ))}

              <input
                value={ccDraft}
                onChange={(e) => {
                  const value = e.target.value;
                  setCcDraft(value);

                  if (!value.trim()) {
                    setCcError("");
                  }
                }}
                onKeyDown={handleCcKeyDown}
                onBlur={commitCcDraft}
                className="contact-email-recipient-input"
              />

              {ccSuggestions.length > 0 && (
                <div className="contact-email-cc-suggestions">
                  {ccSuggestions.map((item) => (
                    <button
                      type="button"
                      key={item.id || item.email}
                      className="contact-email-cc-suggestion"
                      onMouseDown={() => addCcRecipient(item.email)}
                    >
                      <div className="contact-email-cc-suggestion-avatar">
                        {item.name
                          ? item.name.charAt(0).toUpperCase()
                          : item.email.charAt(0).toUpperCase()}
                      </div>
                      <div className="contact-email-cc-suggestion-name">
                        {item.name || item.email}
                      </div>
                      <small>{item.email}</small>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {ccError && <div className="contact-emails-error-cc">{ccError}</div>}

          <div className="contact-email-compose-field-row">
            <span>{t("From")}</span>
            <strong>{composeForm.from || ""}</strong>
          </div>
        </div>

        <input
          id="contact-email-compose-attachment"
          type="file"
          multiple
          className="d-none"
          onChange={onAttachmentChange}
        />

        <div
          id="contact-email-compose-toolbar"
          className="contact-email-compose-toolbar"
        >
          <button
            type="button"
            className="contact-email-compose-attachment-button"
            onClick={() =>
              document
                .getElementById("contact-email-compose-attachment")
                ?.click()
            }
          >
            <BsPaperclip />
          </button>

          <span className="contact-email-compose-toolbar-divider" />

          <button type="button" className="ql-bold">
            <BsTypeBold />
          </button>
          <button type="button" className="ql-italic">
            <BsTypeItalic />
          </button>
          <button type="button" className="ql-underline">
            <BsTypeUnderline />
          </button>
          <button type="button" className="ql-list" value="ordered">
            <BsListOl />
          </button>
          <button type="button" className="ql-list" value="bullet">
            <BsJustifyLeft />
          </button>
        </div>

        {composeError && (
          <div className="contact-emails-error">{composeError}</div>
        )}

        <div className="contact-email-compose-body-scroll">
          {composeAttachments.length > 0 && (
            <div className="contact-email-compose-attachments">
              {composeAttachments.map((file, index) => (
                <button
                  type="button"
                  key={`${file.name}-${index}`}
                  className="contact-email-compose-attachment"
                  onClick={() => onRemoveAttachment(index)}
                  title={t("Remove attachment")}
                >
                  <BsPaperclip />
                  <span>{file.name}</span>
                  <BsX />
                </button>
              ))}
            </div>
          )}

          <ReactQuill
            theme="snow"
            value={composeForm.body}
            onChange={(value) =>
              setComposeForm((form) => ({ ...form, body: value }))
            }
            modules={composeEditorModules}
            className="contact-email-compose-editor"
          />

          {composeQuotedBody && (
            <div
              className="contact-email-quoted-body"
              dangerouslySetInnerHTML={{ __html: composeQuotedBody }}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};
